import { Placeholder, Spinner } from '@vkontakte/vkui';
import type { ReactNode } from 'react';
import { t } from '../../i18n';
import { HexIcon } from '../icons';
import { Action } from './Action';
import { Loader } from './Loader';
import styles from './ui.module.css';

/**
 * Три состояния, которые есть у каждого экрана с загрузкой: ждём, не вышло,
 * пусто. Собраны в одном месте, чтобы формулировки не разъезжались от панели
 * к панели: человек, увидевший «Ничего не нашли» в реестре и в базе внешек,
 * должен понимать, что это одно и то же состояние.
 *
 * Собраны на СВОИХ слотах Placeholder — icon, title, action, — а не стопкой
 * детей с ручными marginBottom, как было раньше. Своя раскладка компонента
 * при таком обходе не работала: спиннер и пустота липли под шапку вместо
 * середины экрана. Из тех же соображений stretched стоит там, где на экране
 * и правда больше ничего нет, и снят у пустого списка — над ним остаются
 * поиск и переключатель режимов.
 */

export function LoadingState({
  text = t('ui.state.loading'),
  inline = false,
}: {
  text?: string;
  /**
   * Не растягивать на экран. Ставят там, где грузится кусок, а остальное
   * содержимое уже на месте: растянутый плейсхолдер в такой середине
   * выглядит так, будто отвалился весь экран.
   */
  inline?: boolean;
}) {
  return (
    <Placeholder stretched={!inline} icon={inline ? <Spinner /> : <Loader />}>
      <span className={styles.stateText}>{text}</span>
    </Placeholder>
  );
}

interface ErrorStateProps {
  message: string;
  onRetry?: () => void;
  title?: string;
}

export function ErrorState({ message, onRetry, title = t('ui.state.error') }: ErrorStateProps) {
  return (
    <Placeholder
      stretched
      icon={<HexIcon size={44} />}
      title={<span className={styles.stateTitle}>{title}</span>}
      action={
        onRetry ? (
          <Action tone="accent" size="m" onClick={onRetry}>
            {t('common.action.retry')}
          </Action>
        ) : undefined
      }
    >
      <span className={styles.stateText}>{message}</span>
    </Placeholder>
  );
}

interface EmptyStateProps {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({ title, children, action }: EmptyStateProps) {
  return (
    <Placeholder
      icon={<HexIcon size={44} />}
      title={<span className={styles.stateTitle}>{title}</span>}
      action={action}
    >
      {children === undefined ? undefined : <span className={styles.stateText}>{children}</span>}
    </Placeholder>
  );
}

/**
 * Экран «сюда нельзя». Отдельно от `EmptyState` по смыслу: это не «ничего не
 * нашли», а «вам сюда не положено» — пустая панель без объяснения хуже
 * честного отказа. Форма при этом та же: слоты `Placeholder`, а не стопка с
 * отступом по месту.
 */
export function NoAccess({ children }: { children?: ReactNode }) {
  return (
    <Placeholder title={<span className={styles.stateTitle}>{t('ui.state.noAccess.title')}</span>}>
      <span className={styles.stateText}>{children ?? t('ui.state.noAccess.text')}</span>
    </Placeholder>
  );
}
